import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { type ReactNode, useRef, useSyncExternalStore } from 'react';

import { IconButton } from './index';

const query = '(max-width:640px)';

const subscribe = (notify: () => void) => {
  const media = matchMedia(query);

  media.addEventListener('change', notify);

  return () => media.removeEventListener('change', notify);
};

export function useMobileLayout() {
  return useSyncExternalStore(
    subscribe,
    () => matchMedia(query).matches,
    () => false,
  );
}

/** Full-screen mobile details with the same content inline on desktop. */
export function MobileDetails({
  open,
  title,
  closeLabel,
  onClose,
  busy = false,
  status,
  prompt,
  children,
}: {
  open: boolean;
  title: string;
  closeLabel: string;
  onClose: () => void;
  busy?: boolean;
  status?: ReactNode;
  prompt?: ReactNode;
  children: ReactNode;
}) {
  const mobile = useMobileLayout(),
    closeButton = useRef<HTMLButtonElement>(null),
    returnFocus = useRef<HTMLElement | null>(null);

  if (!mobile) {
    return <>{children}</>;
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={next => {
        if (!next && !busy) {
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="ui-mobile-details-backdrop" />
        <Dialog.Content
          className="ui-mobile-details"
          aria-describedby={undefined}
          onOpenAutoFocus={event => {
            event.preventDefault();

            returnFocus.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;

            closeButton.current?.focus();
          }}
          onCloseAutoFocus={event => {
            event.preventDefault();
            const target = returnFocus.current;

            if (target?.isConnected) {
              target.focus();
            }
          }}
          onEscapeKeyDown={event => {
            if (busy) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={event => event.preventDefault()}
        >
          <header className="ui-mobile-details-header">
            <div className="ui-mobile-details-heading">
              <div>
                <Dialog.Title>{title}</Dialog.Title>
                {status && (
                  <div className="ui-mobile-details-status" role="status">
                    {status}
                  </div>
                )}
              </div>
              <IconButton ref={closeButton} label={closeLabel} disabled={busy} onClick={onClose}>
                <X aria-hidden="true" />
              </IconButton>
            </div>
            {prompt}
          </header>
          <div className="ui-mobile-details-body">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
